import { Intent as BlueprintIntent } from '@blueprintjs/core';
import { WarningAlt } from '@carbon/icons-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Icon,
  LoadingIcon,
  Modal,
  Selector,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  LEAVE_ORGANIZATION_DIALOG_CANCEL,
  LEAVE_ORGANIZATION_DIALOG_SELECTOR,
  LEAVE_ORGANIZATION_DIALOG_SELECTOR_ITEM,
  LEAVE_ORGANIZATION_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useLeaveOrganizationMutation,
  useTransferOrganizationOwnershipMutation,
} from 'src/ui/graph/types';
import { useProfile } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import {
  useInvalidateOrganization,
  useInvalidateOrganizationMembers,
  useIsOrganizationOwner,
  useOrganization,
} from 'src/ui/hooks/organization';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(DialogType.LEAVE_ORGANIZATION);

export const LeaveOrganizationDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();

  const { data: profile } = useProfile();
  const { membersByEmail } = useOrganization(org);
  const isOwner = useIsOrganizationOwner(org);
  const invalidateOrg = useInvalidateOrganization(org);
  const invalidateMembers = useInvalidateOrganizationMembers(org);

  const memberItems = React.useMemo(
    () =>
      Object.values(membersByEmail)
        .filter((member) => member.email !== profile?.profile?.email)
        .map<SelectItem<null>>((member) => ({
          value: member.email,
          text: `${member.firstName} ${member.lastName}`,
          subText: member.email,
        })),
    [membersByEmail, profile]
  );

  const isOpen = useAppSelector(isOpenSelector);
  const onError = useOnError();
  const onMutate = useOnMutate();

  const [owner, setOwner] = React.useState<string>('');

  React.useEffect(() => {
    if (isOpen) {
      setOwner('');
    }
  }, [isOpen]);

  const {
    isLoading: isLeaveLoading,
    mutate: leaveOrganization,
  } = useLeaveOrganizationMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data) => {
      invalidateOrg();

      if (!data.leaveOrganization?.success) {
        dispatch(
          queueNotification(
            data.leaveOrganization?.error ||
              copy.components.organization.leaveDialog.notifications.error
          )
        );
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.organization.leaveDialog.notifications.success,
        })
      );
      handleClose();
    },
  });
  const {
    isLoading: isTransferLoading,
    mutate: transferOwnership,
  } = useTransferOrganizationOwnershipMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data) => {
      invalidateOrg();
      invalidateMembers();

      if (!data.transferOrganizationOwnership?.success) {
        dispatch(
          queueNotification(
            data.transferOrganizationOwnership?.error ||
              copy.components.organization.transferDialog.notifications.error
          )
        );
        return;
      }

      leaveOrganization({ org_domain: org });
    },
  });

  const isLoading = isLeaveLoading || isTransferLoading;

  const handleClose = () => {
    dispatch(closedDialog());
    setOwner('');
  };

  const handleSubmit = () => {
    if ((isOwner && !owner) || isLoading) {
      return;
    }

    if (isOwner) {
      transferOwnership({ org_domain: org, email: owner });
    } else {
      leaveOrganization({ org_domain: org });
    }
  };

  const isSubmitDisabled = (isOwner && !owner) || isLoading;

  return (
    <Modal
      canOutsideClickClose={false}
      className="organization-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={LEAVE_ORGANIZATION_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.organization.leaveDialog.buttons.cancel}
          </Button>
          <Button
            disabled={isSubmitDisabled}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={LEAVE_ORGANIZATION_DIALOG_SUBMIT}
            variant="primary-gray"
          >
            {isOwner
              ? copy.components.organization.leaveDialog.buttons
                  .submitTransferAndLeave
              : copy.components.organization.leaveDialog.buttons.submitLeave}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.organization.leaveDialog.title}
    >
      <div className="organization-dialog-body">
        {isOwner ? (
          <>
            <div className="organization-dialog-warning">
              <Icon IconComponent={WarningAlt} size="lg" />
              <div>
                <Body size="md">
                  {copy.components.organization.leaveDialog.warning.line_1}
                </Body>
                <Body size="md">
                  {copy.components.organization.leaveDialog.warning.line_2}
                </Body>
                <Body size="md">
                  {copy.components.organization.leaveDialog.warning.line_3}
                </Body>
              </div>
            </div>

            <Selector.Single<null, SelectItem, null | SelectItem>
              chipInputProps={{
                className: 'organization-dialog-chip-selector',
                label: copy.components.organization.leaveDialog.help.owner,
                size: 'md',
                testId: LEAVE_ORGANIZATION_DIALOG_SELECTOR,
                values: owner ? [owner] : [],
              }}
              filterable
              items={memberItems}
              itemTestId={LEAVE_ORGANIZATION_DIALOG_SELECTOR_ITEM}
              onSelect={(selected) => setOwner(selected?.value || '')}
              popoverProps={{
                portalClassName: 'organization-dialog-menu-portal',
              }}
              selected={
                memberItems.find((item) => item.value === owner) || null
              }
            />
          </>
        ) : (
          <Body size="md">
            {copy.components.organization.leaveDialog.help.member}
          </Body>
        )}
      </div>
    </Modal>
  );
};
